// Growem: LAYER 1 of the evidence-based habit system. Two PURE read-models over data that
// already exists (no new DB table, no new store): `todayTasks` derives today's if-then habit
// cues (auto-completing from the real metric that backs each one), and `weeklyWeight` derives
// the honest 7-day rolling bodyweight average. Both are unit-tested in tasks.test.ts.
//
// Evidence base (why this shape):
//  - Implementation intentions / if-then cues bound to a stable daily trigger roughly double
//    follow-through (Gollwitzer & Sheeran 2006). Every row here is phrased "Al <cue>: <action> ->".
//  - Time-gated, most-impactful-first ordering avoids the giant-checklist fatigue that kills habit
//    apps; we cap the card at ~3 visible rows and rotate the pinned #1 by time of day.
//  - Daily scale weight varies 0.5-2 kg from water/glycogen/gut content; only the multi-day average
//    is signal (Hall 2011). `weeklyWeight` gates the direction word behind a noise band so a single
//    day never headlines a trend.
//
// FUTURE iOS NATIVE: each row `todayTasks` returns is a cue the WEB can only render in-app. The
// native iOS app should schedule the in-window, not-done rows as UNUserNotificationCenter LOCAL
// notifications at each row's window-open time (weigh-in/sleep at wake, protein debt at 18:00,
// etc.), using the time-sensitive interruption level, and mirror the undone-count onto the app-icon
// badge. This module already computes window + priority per row, so the native scheduler can consume
// the same Task[] the card renders. (Layer 1 is in-app only; no OS permission is requested here.)

import type { DayState, Meal } from '../types'
import { GOALS } from '../data/plan'
import { sessionProgress, proteinDebt } from './rules'

// A protein "dose" (leucine-threshold-sized feeding). Meals at/above this are counted as tomas;
// this is DISPLAY-ONLY color and is a LOWER BOUND: quick-add protein chips bump day.prot without
// creating a meal row, so this undercounts. Never use it to contradict day.prot (the authoritative
// completion signal).
const PROTEIN_DOSE_G = 35

// Week-over-week average change (kg) below which we refuse to name a direction: within this band
// the move is indistinguishable from water noise even on a 7-day average, so we say "estable".
const WEIGHT_NOISE_KG = 0.2

/** Which habit a task represents; also the discriminant the card uses to wire the one-tap action. */
export type TaskKind = 'weigh' | 'sleep' | 'train' | 'protein' | 'water' | 'creatine'

export interface Task {
  id: TaskKind
  kind: TaskKind
  /** If-then implementation-intention cue, e.g. "Al despertar: pésate en ayunas ->". */
  label: string
  /** Optional honest secondary microcopy under the cue. */
  sub?: string
  /** DONE derived LIVE from the real metric that backs the habit (never a separate store). */
  done: boolean
  /** Whether the task's time window is currently open (drives visibility + native fire-time). */
  inWindow: boolean
  /** Lower = higher rank; the pinned #1 has the lowest number. See rotation logic below. */
  priority: number
  /** Action hint for the card: which existing store/ui handler this row's tap should fire. */
  action: TaskKind
}

/** "HH:MM" -> hour (0..23), or null when unparseable. Used to elevate the train pin ~1 h pre-session. */
function hourFromHHMM(t: string | null): number | null {
  if (!t) return null
  const m = t.match(/^(\d{1,2}):(\d{2})/)
  if (!m) return null
  const h = Number(m[1])
  return h >= 0 && h <= 23 ? h : null
}

/**
 * Parse a free-text sleep stretch like "3h30" / "7h" / "7,5" -> hours as a decimal, or null.
 * No parser existed in the codebase; the value is free-text from a prompt so the format isn't
 * guaranteed. Used ONLY for encouraging sub-copy: sleep completion is "recorded", not ">= goal"
 * (you can't re-sleep the night, so the honest done-signal is that the stretch is logged).
 */
export function parseSleepHours(s: string | null): number | null {
  if (!s) return null
  const m = s.match(/(\d+(?:[.,]\d+)?)\s*h(?:\s*(\d+))?/i)
  if (m) {
    const h = Number(m[1].replace(',', '.'))
    const min = m[2] ? Number(m[2]) : 0
    return Number.isFinite(h) ? h + min / 60 : null
  }
  const n = Number(s.replace(',', '.'))
  return Number.isFinite(n) ? n : null
}

/**
 * Pure task model for TODAY. Reads only today's `day`, today's `meals`, the local `hr` (0..23),
 * and `planIndex` (0..5 Mon-Sat, null Sunday). Returns EVERY applicable habit row for the day
 * (done or not, in-window or not) so the card can both render the visible subset and compute the
 * "al día · n/total" collapsed summary. The card is responsible for filtering to
 * `inWindow && !done`, sorting by `priority`, and capping at 3.
 *
 * Applicable set: 6 rows Mon-Sat, 5 on Sunday (no train row when planIndex == null = rest).
 */
export function todayTasks(day: DayState, meals: Meal[], hr: number, planIndex: number | null): Task[] {
  const tasks: Task[] = []
  const morning = hr < GOALS.morningHour           // fasted weigh-in + sleep-log window
  const evening = hr >= GOALS.proteinDebtHour      // reuses the existing 18:00 protein-debt boundary

  // ---- 1 · weigh-in (fasted, morning only) ----------------------------------------------------
  // FIX #1 morning gating lives here: the task is only IN-WINDOW before GOALS.morningHour. There is
  // no capture-time on bodyweight_kg, so we can't retroactively prove a stored weight was a morning
  // weigh-in; we gate the PROMPT window prospectively instead of flagging past data.
  tasks.push({
    id: 'weigh', kind: 'weigh', action: 'weigh',
    done: day.weight != null,
    inWindow: morning,
    priority: 10 - (morning ? 100 : 0),
    label: 'Al despertar: pésate en ayunas →',
    sub: `antes de las ${GOALS.morningHour}:00 y sin desayunar; el peso que cuenta es el de la mañana.`,
  })

  // ---- 2 · sleep (morning log) ----------------------------------------------------------------
  const sleptH = parseSleepHours(day.sleep)
  const sleepShort = sleptH != null && sleptH < GOALS.sleepStretchGoalH
  tasks.push({
    id: 'sleep', kind: 'sleep', action: 'sleep',
    done: day.sleep != null,
    inWindow: morning,
    priority: 20 - (morning ? 80 : 0),
    label: 'Recién levantado: anota tu tramo de sueño más largo →',
    sub: sleepShort
      ? `menos de ${GOALS.sleepStretchGoalH} h seguidas; prioriza acostarte más temprano hoy.`
      : `la meta son ${GOALS.sleepStretchGoalH} h de un tirón; el dato de hoy mide tu recuperación.`,
  })

  // ---- 3 · train + log (session sets) ---------------------------------------------------------
  // Only exists on training days. Sunday (planIndex == null) => no row => rest. In-progress sessions
  // stay visible with a "remátala" variant.
  const { doneN, totN } = sessionProgress(day, planIndex)
  if (planIndex != null && totN > 0) {
    const inProgress = doneN > 0 && doneN < totN
    // Elevate the train pin from ~1 h before a committed trainAt, else from GOALS.trainBoostHour.
    const trainHr = hourFromHHMM(day.trainAt)
    const boostFrom = trainHr != null ? trainHr - 1 : GOALS.trainBoostHour
    const label = inProgress
      ? `Vas ${doneN}/${totN} de la sesión: remátala →`
      : day.trainAt
        ? `Hoy entrenas ${day.trainAt}: abre la sesión y registra cada serie →`
        : 'Antes de entrenar: fija tu hora y abre la sesión →'
    tasks.push({
      id: 'train', kind: 'train', action: 'train',
      done: doneN === totN,
      inWindow: true,
      priority: 30 - (hr >= boostFrom ? 70 : 0),
      label,
    })
  }

  // ---- 4 · protein ----------------------------------------------------------------------------
  // Authoritative completion = day.prot >= proteinMin. Evening (>=18:00) with a real debt escalates
  // to the pin. `proteinDebt` already returns 0 before 18:00, so debt>0 implies evening.
  const debt = proteinDebt(day.prot, hr)
  const doses = meals.filter((m) => m.prot >= PROTEIN_DOSE_G).length
  tasks.push({
    id: 'protein', kind: 'protein', action: 'protein',
    done: day.prot >= GOALS.proteinMin,
    inWindow: true,
    priority: 40 - (debt > 0 ? 90 : 0),
    label: debt > 0
      ? `Antes de dormir: te faltan ${debt} g de proteína, comida real primero →`
      : `En cada comida: sube la proteína a ${GOALS.proteinMin} g. Vas ${day.prot} →`,
    sub: debt > 0
      ? undefined
      : `${doses} ${doses === 1 ? 'toma' : 'tomas'} de ≥${PROTEIN_DOSE_G} g registradas; reparte hasta ${GOALS.proteinMax} g.`,
  })

  // ---- 5 · hydration --------------------------------------------------------------------------
  tasks.push({
    id: 'water', kind: 'water', action: 'water',
    done: day.water >= GOALS.waterMinMl,
    inWindow: true,
    priority: 50,
    label: `Ten el vaso a la vista: bebe hasta ${(GOALS.waterMinMl / 1000).toFixed(1)} L. Vas ${(day.water / 1000).toFixed(1)} L →`,
  })

  // ---- 6 · creatine ---------------------------------------------------------------------------
  tasks.push({
    id: 'creatine', kind: 'creatine', action: 'creatine',
    done: day.creatine === true,
    inWindow: true,
    priority: 60 - (evening ? 60 : 0),
    label: evening
      ? `No la dejes para mañana: ${GOALS.creatineG} g de creatina hoy →`
      : `Con tu batido o comida: toma la creatina, ${GOALS.creatineG} g →`,
  })

  return tasks
}

/** The card's derived view: which rows to show (in-window, undone, ranked, capped) + the summary. */
export interface TasksView {
  visible: Task[]
  doneCount: number
  total: number
}

/**
 * Pure selector the card uses: rank in-window+undone rows by priority, cap at `cap` (default 3),
 * and report done/total across ALL applicable habits (missed morning tasks count as not-done for
 * honesty but are no longer surfaced as rows once their window closes).
 */
export function selectTasksView(tasks: Task[], cap = 3): TasksView {
  const visible = tasks
    .filter((t) => t.inWindow && !t.done)
    .sort((a, b) => a.priority - b.priority)
    .slice(0, cap)
  return { visible, doneCount: tasks.filter((t) => t.done).length, total: tasks.length }
}

// ---------- FIX #2 · weekly rolling bodyweight average ----------------------------------------

export type WeightTrend = 'subiendo' | 'bajando' | 'estable' | 'insuficiente'

export interface WeeklyWeight {
  /** 7-day rolling average of the most recent logged weigh-ins (kg, 1 decimal), or null if none. */
  avg7: number | null
  /** Week-over-week change of the average (kg, 1 decimal): this window vs the prior window. Null
   *  until there is enough history to compute a prior window honestly. */
  deltaKg: number | null
  /** Direction word, gated behind the noise band; 'insuficiente' until `enough` is true. */
  trend: WeightTrend
  /** True only when both windows have >= 3 weigh-ins AND we can name a direction responsibly. */
  enough: boolean
  /** How many logged weigh-ins were available (for honest "n de N" microcopy). */
  count: number
  /** How many weigh-ins are in the CURRENT window (min(count, 7)). Drives the honest label
   *  "promedio de tus N pesajes" so a 1-2 sample mean is never dressed up as a "7 dias" average. */
  windowN: number
}

function mean(xs: number[]): number {
  return xs.reduce((a, b) => a + b, 0) / xs.length
}
function round1(n: number): number {
  return Math.round(n * 10) / 10
}

/**
 * Pure read-model over `day.weight` across all logged days. Takes the most recent up to 7 logged
 * weigh-ins as the current window and the 7 before that as the prior window; the average of each is
 * compared. The direction word is emitted ONLY when both windows have >= 3 samples and the average
 * change clears WEIGHT_NOISE_KG: otherwise we say 'estable' (in-band) or 'insuficiente' (not enough
 * data). This keeps a single high-water day from ever headlining a trend.
 *
 * NOTE: there is no capture-hour on bodyweight_kg, so this cannot exclude a stray evening weigh-in;
 * the 7-day averaging is what makes it robust to that noise rather than per-point filtering.
 */
export function weeklyWeight(days: Record<string, DayState>): WeeklyWeight {
  const logged = Object.values(days)
    .filter((d): d is DayState & { weight: number } => d.weight != null)
    .sort((a, b) => (a.day < b.day ? -1 : a.day > b.day ? 1 : 0))
    .map((d) => d.weight)
  const count = logged.length
  if (count === 0) return { avg7: null, deltaKg: null, trend: 'insuficiente', enough: false, count: 0, windowN: 0 }

  const recent = logged.slice(-7)
  const prior = logged.slice(-14, -7)
  const avg7 = round1(mean(recent))

  let deltaKg: number | null = null
  let trend: WeightTrend = 'insuficiente'
  let enough = false
  if (recent.length >= 3 && prior.length >= 3) {
    const raw = mean(recent) - mean(prior)
    deltaKg = round1(raw)
    enough = true
    trend = raw > WEIGHT_NOISE_KG ? 'subiendo' : raw < -WEIGHT_NOISE_KG ? 'bajando' : 'estable'
  }
  return { avg7, deltaKg, trend, enough, count, windowN: recent.length }
}
